// src/pages/Analytics.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  DatePicker,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  Segmented,
  Avatar,
  Button,
  Empty,
  message,
  Row,
  Col,
  Tooltip,
  Progress,
  Divider,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/it';
import { 
  ReloadOutlined, 
  BarChartOutlined,
  UserOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LineChartOutlined,
  RiseOutlined,
  FallOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import StandardPage from '../components/Layout/StandardPage';
import CreateActivityDrawer from '../components/activities/CreateActivityDrawer';

import { listCustomers, type CustomerDTO } from '../services/customers';
import { listActivitiesByCustomer, listAllActivities, type ActivityDTO } from '../services/activities';
import { renderAntdIcon } from '../utils/antdIcon';

dayjs.locale('it');
dayjs.extend(isoWeek);
dayjs.extend(relativeTime);

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

/* -------------------------- helpers -------------------------- */

const whenOf = (a: ActivityDTO) => a.appointment?.startAt ?? a.occurredAt;
const typeCodeOf = (a: ActivityDTO) => a.activityType?.code || a.type || 'EXTRA';
const typeLabelOf = (a: ActivityDTO) => a.activityType?.label || a.type || 'EXTRA';
const hasAppointment = (a: ActivityDTO) => !!a.appointment;
const isConfirmed = (a: ActivityDTO) => !!a.appointment?.confirmed && !a.appointment?.cancelled;
const isCancelled = (a: ActivityDTO) => !!a.appointment?.cancelled;

type CustomerAgg = {
  customerId: number;
  customerName: string;
  total: number;
  monthCurrent: number;
  activeMonths: number;
  lastAt?: string;
  avgPerMonth: number;
  topTypes: { label: string; count: number; icon?: string | null }[];
  trend: 'up' | 'down' | 'stable';
  trendPct: number;
};

type Bucket = { key: string; label: string; count: number };

function buildTrendBuckets(items: ActivityDTO[], grouping: 'month' | 'week') {
  const map = new Map<string, number>();
  const labelMap = new Map<string, string>();

  items.forEach(a => {
    const d = dayjs(whenOf(a));
    const key =
      grouping === 'week'
        ? `${d.year()}-W${String(d.isoWeek()).padStart(2, '0')}`
        : `${d.year()}-${String(d.month() + 1).padStart(2, '0')}`;
    const label =
      grouping === 'week'
        ? `W${String(d.isoWeek()).padStart(2, '0')}`
        : d.format('MMM');

    map.set(key, (map.get(key) || 0) + 1);
    labelMap.set(key, label);
  });

  return Array.from(map.entries())
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([key, count]) => ({ key, count, label: labelMap.get(key) || key })) as Bucket[];
}

/* --------------- Enhanced chart components --------------- */

// Minimalist line chart
const LineChart: React.FC<{ data: Bucket[]; height?: number }> = ({ data, height = 180 }) => {
  if (data.length === 0) return <Empty description="Nessun dato" />;
  
  const max = Math.max(1, ...data.map(d => d.count));
  const min = Math.min(0, ...data.map(d => d.count));
  const range = max - min || 1;
  
  const points = data.map((d, i) => {
    const x = (i / Math.max(1, data.length - 1)) * 100;
    const y = 100 - ((d.count - min) / range) * 80;
    return `${x},${y}`;
  }).join(' ');

  const current = data[data.length - 1]?.count || 0;
  const previous = data[data.length - 2]?.count || 0;
  const trend = current > previous ? 'up' : current < previous ? 'down' : 'stable';
  const trendPct = previous > 0 ? Math.abs(((current - previous) / previous) * 100) : 0;

  return (
    <div style={{ position: 'relative' }}>
      {/* Header with trend */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 24 
      }}>
        <div>
          <div style={{ fontSize: 32, fontWeight: 300, color: '#262626' }}>
            {current.toLocaleString()}
          </div>
          <Text type="secondary" style={{ fontSize: 14 }}>
            Attività nel periodo
          </Text>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 4,
            color: trend === 'up' ? '#52c41a' : trend === 'down' ? '#ff4d4f' : '#8c8c8c',
          }}>
            {trend === 'up' && <RiseOutlined />}
            {trend === 'down' && <FallOutlined />}
            <span style={{ fontSize: 16, fontWeight: 500 }}>
              {trendPct.toFixed(1)}%
            </span>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            vs periodo precedente
          </Text>
        </div>
      </div>

      {/* Chart */}
      <div style={{ height, position: 'relative', background: '#fafafa', borderRadius: 8, padding: 16 }}>
        <svg width="100%" height="100%" style={{ overflow: 'visible' }}>
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map(y => (
            <line
              key={y}
              x1="0%"
              x2="100%"
              y1={`${y}%`}
              y2={`${y}%`}
              stroke="#f0f0f0"
              strokeWidth={1}
            />
          ))}
          
          {/* Gradient fill */}
          <defs>
            <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#1677ff" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#1677ff" stopOpacity={0} />
            </linearGradient>
          </defs>
          
          {/* Area fill */}
          <polygon
            points={`0,100 ${points} 100,100`}
            fill="url(#areaGradient)"
          />
          
          {/* Main line */}
          <polyline
            points={points}
            fill="none"
            stroke="#1677ff"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          
          {/* Data points with values */}
          {data.map((d, i) => {
            const x = (i / Math.max(1, data.length - 1)) * 100;
            const y = 100 - ((d.count - min) / range) * 80;
            return (
              <g key={i}>
                <circle
                  cx={`${x}%`}
                  cy={`${y}%`}
                  r={4}
                  fill="#1677ff"
                  stroke="#fff"
                  strokeWidth={2}
                />
                {/* Value label above point */}
                <text
                  x={`${x}%`}
                  y={`${y - 12}%`}
                  textAnchor="middle"
                  fontSize="11"
                  fill="#262626"
                  fontWeight="500"
                >
                  {d.count}
                </text>
              </g>
            );
          })}
        </svg>
        
        {/* X-axis labels */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginTop: 8,
          fontSize: 12,
          color: '#8c8c8c',
        }}>
          {data.filter((_, i) => i % Math.ceil(data.length / 6) === 0).map((d, i) => (
            <span key={i}>{d.label}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

// Clean metric card
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  trend?: { direction: 'up' | 'down' | 'stable'; value: number };
  subtitle?: string;
  icon?: React.ReactNode;
  color?: string;
}> = ({ title, value, trend, subtitle, icon, color = '#1677ff' }) => (
  <Card
    style={{
        minHeight: 140,
      borderRadius: 16,
      border: 'none',
      background: 'linear-gradient(135deg, #f6f9fc 0%, #ffffff 100%)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    }}
    bodyStyle={{ padding: '24px' }}
  >
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <Text type="secondary" style={{ fontSize: 13, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {title}
        </Text>
        <div style={{ fontSize: 32, fontWeight: 300, color: '#262626', lineHeight: 1.2, marginTop: 4 }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        {subtitle && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {subtitle}
          </Text>
        )}
        {trend && (
          <div style={{ 
            marginTop: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            color: trend.direction === 'up' ? '#52c41a' : trend.direction === 'down' ? '#ff4d4f' : '#8c8c8c',
          }}>
            {trend.direction === 'up' && <RiseOutlined />}
            {trend.direction === 'down' && <FallOutlined />}
            <span style={{ fontSize: 14, fontWeight: 500 }}>
              {trend.value.toFixed(1)}%
            </span>
          </div>
        )}
      </div>
      {icon && (
        <div style={{
          padding: 12,
          borderRadius: 12,
          background: `${color}15`,
          color: color,
          fontSize: 20,
        }}>
          {icon}
        </div>
      )}
    </div>
  </Card>
);

// Clean horizontal bar for rankings
const RankingBar: React.FC<{ 
  rank: number; 
  label: string; 
  value: number; 
  max: number; 
  percentage: number;
}> = ({ rank, label, value, max, percentage }) => (
  <div style={{ 
    display: 'flex', 
    alignItems: 'center', 
    gap: 16, 
    padding: '12px 0',
    borderBottom: '1px solid #f5f5f5',
  }}>
    <div style={{
      width: 32,
      height: 32,
      borderRadius: 16,
      background: rank <= 3 ? '#1677ff' : '#f0f0f0',
      color: rank <= 3 ? '#fff' : '#8c8c8c',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 14,
      fontWeight: 600,
    }}>
      {rank}
    </div>
    
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <Text style={{ fontWeight: 500, fontSize: 14 }}>{label}</Text>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {value} ({percentage.toFixed(1)}%)
        </Text>
      </div>
      
      <div style={{ 
        height: 6, 
        background: '#f5f5f5', 
        borderRadius: 3, 
        overflow: 'hidden' 
      }}>
        <div
          style={{
            height: '100%',
            width: `${(value / max) * 100}%`,
            background: rank <= 3 ? '#1677ff' : '#bfbfbf',
            borderRadius: 3,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  </div>
);

/* ------------------------------ page ------------------------------ */

const AnalyticsPage: React.FC = () => {
  const navigate = useNavigate();

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState<number | undefined>(undefined);
  const openEdit = (id: number) => { setEditId(id); setEditOpen(true); };

  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<CustomerDTO[]>([]);
  const [byCustomer, setByCustomer] = useState<Record<number, ActivityDTO[]>>({});

  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(6, 'month').startOf('day'),
    dayjs().endOf('day'),
  ]);
  const [grouping, setGrouping] = useState<'month' | 'week'>('month');

const fetchAll = useCallback(async () => {
  setLoading(true);
  try {
    // 1 sola fetch per clienti + 1 sola fetch (paginata) per tutte le attività
    const [cs, acts] = await Promise.all([
      listCustomers(),
      listAllActivities({ size: 1000, sort: 'occurredAt,desc', maxPages: 50 }),
    ]);

    const activeCs = cs.filter(c => c.isActive);
    setCustomers(activeCs);

    // Raggruppa attività per customerId
    const grouped: Record<number, ActivityDTO[]> = {};
    for (const a of acts) {
      if (a.customerId == null) continue; // skip orfani
      (grouped[a.customerId] ||= []).push(a);
    }

    // Assicura la presenza di tutti i clienti attivi anche senza attività
    for (const c of activeCs) {
      if (!grouped[c.id]) grouped[c.id] = [];
    }

    setByCustomer(grouped);
  } catch (e: any) {
    message.error(e?.message || 'Errore caricamento analytics');
  } finally {
    setLoading(false);
  }
}, []);


  useEffect(() => { fetchAll(); }, [fetchAll]);

  const activities = useMemo(() => {
    const [start, end] = range;
    const s = start.valueOf();
    const e = end.valueOf();
    const all: ActivityDTO[] = [];
    Object.values(byCustomer).forEach(list => {
      list.forEach(a => {
        const t = dayjs(whenOf(a)).valueOf();
        if (t >= s && t <= e) all.push(a);
      });
    });
    return all.sort((a, b) => dayjs(whenOf(b)).valueOf() - dayjs(whenOf(a)).valueOf());
  }, [byCustomer, range]);

  const kpi = useMemo(() => {
    const ids = new Set<number>();
    let withAppt = 0, confirmed = 0, cancelled = 0;
    activities.forEach(a => {
      if (a.customerId != null) ids.add(a.customerId);
      if (hasAppointment(a)) withAppt++;
      if (isConfirmed(a)) confirmed++;
      if (isCancelled(a)) cancelled++;
    });
    const activeCustomers = ids.size;
    const total = activities.length;
    const avgPerCustomer = activeCustomers ? (total / activeCustomers) : 0;

    return {
      total,
      activeCustomers,
      avgPerCustomer: Number.isFinite(avgPerCustomer) ? avgPerCustomer : 0,
      withApptPct: total ? Math.round((withAppt / total) * 100) : 0,
      confirmed,
      cancelled,
    };
  }, [activities]);

  const trend = useMemo(() => buildTrendBuckets(activities, grouping), [activities, grouping]);

  const typeRanking = useMemo(() => {
    const map = new Map<string, number>();
    activities.forEach(a => {
      map.set(typeLabelOf(a), (map.get(typeLabelOf(a)) || 0) + 1);
    });
    const arr = Array.from(map.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);
    const max = Math.max(1, ...arr.map(x => x.count));
    return { arr, max };
  }, [activities]);

  const customerAgg: CustomerAgg[] = useMemo(() => {
    const [start, end] = range;
    const currMonthKey = dayjs().format('YYYY-MM');

    return customers.map(c => {
      const listAll = byCustomer[c.id] || [];

      const listInRange = listAll.filter(a => {
        const t = dayjs(whenOf(a));
        return t.isAfter(start) && t.isBefore(end);
      });

      const months = new Set(listInRange.map(a => dayjs(whenOf(a)).format('YYYY-MM')));
      const inCurrMonth = listInRange.filter(a => dayjs(whenOf(a)).format('YYYY-MM') === currMonthKey).length;

      const last = listAll
        .map(a => whenOf(a))
        .filter(Boolean)
        .sort((x, y) => dayjs(y!).valueOf() - dayjs(x!).valueOf())[0];

      const m = new Map<string, { label: string; icon?: string | null; count: number }>();
      listInRange.forEach(a => {
        const k = typeCodeOf(a);
        const cur = m.get(k) || { label: typeLabelOf(a), icon: a.activityType?.icon, count: 0 };
        cur.count++;
        m.set(k, cur);
      });
      const topTypes = Array.from(m.values()).sort((a, b) => b.count - a.count).slice(0, 3);

      const monthsInRange = Math.max(1, end.startOf('month').diff(start.startOf('month'), 'month') + 1);
      const avg = listInRange.length / monthsInRange;

      // Calculate trend
      const halfRangePoint = start.add(end.diff(start) / 2);
      const firstHalf = listInRange.filter(a => dayjs(whenOf(a)).isBefore(halfRangePoint)).length;
      const secondHalf = listInRange.length - firstHalf;
      const trend = secondHalf > firstHalf ? 'up' : secondHalf < firstHalf ? 'down' : 'stable';
      const trendPct = firstHalf > 0 ? Math.abs(((secondHalf - firstHalf) / firstHalf) * 100) : 0;

      return {
        customerId: c.id,
        customerName: c.businessName || c.email || `#${c.id}`,
        total: listInRange.length,
        monthCurrent: inCurrMonth,
        activeMonths: months.size,
        lastAt: last,
        avgPerMonth: Number.isFinite(avg) ? avg : 0,
        topTypes,
        trend: trend as 'up' | 'down' | 'stable',
        trendPct,
      };
    }).sort((a, b) => b.total - a.total);
  }, [customers, byCustomer, range]);

  const topCustomers = useMemo(() => customerAgg.slice(0, 10), [customerAgg]);

  const customerColumns: ColumnsType<CustomerAgg> = [
    {
      title: 'Cliente',
      key: 'cust',
      fixed: 'left',
      width: 280,
      render: (_: any, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar
            size={40}
            style={{
              background: 'linear-gradient(135deg, #1677ff 0%, #69caff 100%)',
              color: '#fff',
              fontSize: 16,
              fontWeight: 600,
            }}
          >
            {(r.customerName || '#')[0].toUpperCase()}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{r.customerName}</div>
            {r.lastAt && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Ultimo: {dayjs(r.lastAt).format('DD/MM/YY')}
              </Text>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Attività',
      key: 'total',
      width: 100,
      sorter: (a, b) => a.total - b.total,
      render: (_: any, r) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#1677ff' }}>
            {r.total}
          </div>
        </div>
      ),
    },
    {
      title: 'Trend',
      key: 'trend',
      width: 80,
      render: (_: any, r) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{
            color: r.trend === 'up' ? '#52c41a' : r.trend === 'down' ? '#ff4d4f' : '#8c8c8c',
            fontSize: 16,
          }}>
            {r.trend === 'up' && <RiseOutlined />}
            {r.trend === 'down' && <FallOutlined />}
            {r.trend === 'stable' && '—'}
          </div>
          {r.trend !== 'stable' && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {r.trendPct.toFixed(0)}%
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Media/mese',
      key: 'avg',
      width: 100,
      sorter: (a, b) => a.avgPerMonth - b.avgPerMonth,
      render: (_, r) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 500 }}>{r.avgPerMonth.toFixed(1)}</div>
        </div>
      ),
    },
    {
      title: 'Attività',
      key: 'types',
      render: (_: any, r) => (
        <Space size={4} wrap>
          {r.topTypes.length === 0 && <Text type="secondary">—</Text>}
          {r.topTypes.slice(0, 2).map(t => (
            <Tag key={t.label} style={{ borderRadius: 12, fontSize: 11 }}>
              {t.label} ({t.count})
            </Tag>
          ))}
        </Space>
      ),
    },
  ];

  const actions = [
    { label: 'Aggiorna', type: 'default' as const, icon: <ReloadOutlined />, onClick: fetchAll },
  ];

  return (
    <>
      <StandardPage 
        title="Analytics" 
        subtitle="Insight e performance" 
        loading={loading} 
        actions={actions}
        showBackButton
        >
        {/* Controls */}
        <Card
          style={{
            borderRadius: 16,
            marginBottom: 24,
            border: 'none',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}
          bodyStyle={{ padding: '20px 24px' }}
        >
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
              <div>
                <Text type="secondary" style={{ 
                  fontSize: 12, 
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'block',
                  marginBottom: 4,
                }}>
                  Periodo di analisi
                </Text>
                <RangePicker
                  allowClear={false}
                  format="DD/MM/YYYY"
                  value={range}
                  onChange={(rng) => {
                    if (!rng || !rng[0] || !rng[1]) return;
                    setRange([rng[0].startOf('day'), rng[1].endOf('day')]);
                  }}
                  style={{ borderRadius: 8 }}
                />
              </div>
              
              <div>
                <Text type="secondary" style={{ 
                  fontSize: 12, 
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'block',
                  marginBottom: 4,
                }}>
                  Raggruppamento
                </Text>
                <Segmented
                  value={grouping}
                  onChange={(v) => setGrouping(v as any)}
                  options={[
                    { label: 'Mensile', value: 'month' },
                    { label: 'Settimanale', value: 'week' }
                  ]}
                  style={{ borderRadius: 8 }}
                />
              </div>
            </div>
            
            <div style={{ textAlign: 'right' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Periodo: {range[0].format('DD/MM/YY')} - {range[1].format('DD/MM/YY')}
              </Text>
              <div style={{ fontSize: 14, fontWeight: 500, marginTop: 2 }}>
                {dayjs(range[1]).diff(range[0], 'days')} giorni
              </div>
            </div>
          </div>
        </Card>

        {/* Key Metrics */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <MetricCard
              title="Attività totali"
              value={kpi.total}
              icon={<BarChartOutlined />}
              color="#1677ff"
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <MetricCard
              title="Clienti attivi"
              value={kpi.activeCustomers}
              icon={<UserOutlined />}
              color="#52c41a"
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <MetricCard
              title="Media per cliente"
              value={kpi.avgPerCustomer.toFixed(1)}
              icon={<LineChartOutlined />}
              color="#722ed1"
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <MetricCard
              title="Shooting svolti"
              value={`${kpi.confirmed}`}
              subtitle={`${((kpi.confirmed / Math.max(1, kpi.total)) * 100).toFixed(1)}% del totale attività`}
              icon={<CheckCircleOutlined />}
              color="#fa8c16"
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {/* Trend Chart */}
          <Col xs={24} lg={16}>
            <Card
              style={{
                borderRadius: 16,
                border: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                height: '100%',
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <div style={{ marginBottom: 16 }}>
                <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                  Trend attività
                </Title>
                <Text type="secondary">
                  Andamento {grouping === 'month' ? 'mensile' : 'settimanale'} nel periodo selezionato
                </Text>
              </div>
              <LineChart data={trend} height={200} />
            </Card>
          </Col>

          {/* Top Activity Types */}
          <Col xs={24} lg={8}>
            <Card
              style={{
                borderRadius: 16,
                border: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                height: '100%',
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <div style={{ marginBottom: 20 }}>
                <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                  Tipi di attività
                </Title>
                <Text type="secondary">Top categorie per volume</Text>
              </div>
              
              {typeRanking.arr.length === 0 ? (
                <Empty description="Nessun dato" />
              ) : (
                <div>
                  {typeRanking.arr.slice(0, 6).map((t, i) => (
                    <RankingBar
                      key={t.label}
                      rank={i + 1}
                      label={t.label}
                      value={t.count}
                      max={typeRanking.max}
                      percentage={(t.count / Math.max(1, kpi.total)) * 100}
                    />
                  ))}
                </div>
              )}
            </Card>
          </Col>
        </Row>

        {/* Customer Analysis */}
        <Card
          style={{
            borderRadius: 16,
            border: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            marginBottom: 24,
          }}
          bodyStyle={{ padding: '24px' }}
        >
          <div style={{ marginBottom: 20 }}>
            <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
              Performance clienti
            </Title>
            <Text type="secondary">
              Analisi dettagliata dell'attività per cliente nel periodo selezionato
            </Text>
          </div>

          {customerAgg.length === 0 ? (
            <Empty description="Nessun cliente attivo nel periodo" />
          ) : (
            <Table<CustomerAgg>
              rowKey={(r) => String(r.customerId)}
              dataSource={customerAgg}
              columns={customerColumns}
              loading={loading}
              size="middle"
              pagination={{ 
                 defaultPageSize: 8,
              pageSizeOptions: [8, 10, 20, 50, 100],
              showSizeChanger: true,
              
                
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} di ${total} clienti`,
              }}
              scroll={{ x: 800 }}
              onRow={(rec) => ({
                onClick: () => navigate(`/customers/${rec.customerId}`),
                style: { 
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                },
                onMouseEnter: (e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                },
                onMouseLeave: (e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                },
              })}
            />
          )}
        </Card>

        {/* Recent Activities - Simplified Table */}
        <Card
          style={{
            borderRadius: 16,
            border: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}
          bodyStyle={{ padding: '24px' }}
        >
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 20,
          }}>
            <div>
              <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                Attività recenti
              </Title>
              <Text type="secondary">
                Elenco delle attività create nel periodo selezionato (fino a 50 mostrate)</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 20, fontWeight: 600, color: '#1677ff' }}>
                  {activities.length}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Totali
                </Text>
              </div>
            </div>
          </div>

          {activities.length === 0 ? (
            <Empty description="Nessuna attività nel periodo selezionato" />
          ) : (
            <Table<ActivityDTO>
              rowKey="id"
              dataSource={activities.slice(0, 50)}
              loading={loading}
              pagination={false}
              size="middle"
              scroll={{ x: 1000 }}
              
              onRow={(rec) => ({
                onClick: () => openEdit(rec.id),
                style: { 
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                },
                onMouseEnter: (e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                },
                onMouseLeave: (e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                },
              })}
              columns={[
                {
                  title: 'Data',
                  key: 'when',
                  width: 140,
                  sorter: (a, b) => dayjs(whenOf(a)).valueOf() - dayjs(whenOf(b)).valueOf(),
                  render: (_: any, a) => (
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>
                        {dayjs(whenOf(a)).format('DD/MM/YY')}
                      </div>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {dayjs(whenOf(a)).format('HH:mm')}
                      </Text>
                    </div>
                  ),
                },
                {
                  title: 'Cliente',
                  key: 'customer',
                  width: 200,
                  render: (_: any, a) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Avatar size={24} style={{ 
                        background: '#f0f5ff', 
                        color: '#1677ff',
                        fontSize: 11,
                        fontWeight: 600,
                      }}>
                        {(a.customerName || '#')[0].toUpperCase()}
                      </Avatar>
                      <span style={{ fontWeight: 500, fontSize: 13 }}>
                        {a.customerName || `#${a.customerId}` || '—'}
                      </span>
                    </div>
                  ),
                },
                {
                  title: 'Attività',
                  key: 'title',
                  filters: typeRanking.arr.slice(0, 10).map(t => ({
                    text: `${t.label} (${t.count})`,
                    value: t.label,
                  })),
                  filterMultiple: true,
                  onFilter: (value, record) => typeLabelOf(record) === value,
                  sorter: (a, b) => (a.name || a.activityType?.label || '').localeCompare(b.name || b.activityType?.label || ''),
                  render: (_: any, a) => (
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>
                        {a.name || a.activityType?.label || 'Attività'}
                      </div>
                      <Tag style={{ fontSize: 10, marginTop: 2 }}>
                        {typeLabelOf(a)}
                      </Tag>
                    </div>
                  ),
                },
                {
                  title: 'Stato',
                  key: 'status',
                  width: 120,
                  render: (_: any, a) => {
                    if (!a.appointment) {
                      return <Tag color="default">Completata</Tag>;
                    }
                    if (a.appointment.cancelled) {
                      return <Tag color="error">Annullata</Tag>;
                    }
                    return a.appointment.confirmed ? 
                      <Tag color="success">Confermata</Tag> : 
                      <Tag color="warning">Annullata</Tag>;
                  },
                },
              ]}
            />
          )}
          
          {activities.length > 50 && (
            <div style={{ 
              textAlign: 'center', 
              marginTop: 16, 
              padding: 16,
              background: '#fafafa',
              borderRadius: 8,
            }}>
              <Text type="secondary">
                Mostrate le prime 50 attività di {activities.length} totali
              </Text>
            </div>
          )}
        </Card>
      </StandardPage>

      {/* Activity Edit Drawer */}
      <CreateActivityDrawer
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditId(undefined);
        }}
        onUpdated={() => {
          setEditOpen(false);
          setEditId(undefined);
          fetchAll();
        }}
        mode="edit"
        activityId={editId}
        lockType
      />
    </>
  );
};

export default AnalyticsPage;